import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AuditTimeline } from '../../components/shared/AuditTimeline';
import { VoucherPrintTemplate } from '../../components/voucher/VoucherPrintTemplate';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
} from '../../lib/utils';
import {
  ArrowLeft,
  Edit,
  Send,
  FileText,
  Download,
  Building2,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Printer,
  CheckCircle,
  XCircle,
  DollarSign,
  X,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

export const VoucherDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  
  // Accounts approval state
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPaidDialog, setShowPaidDialog] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paidAttachment, setPaidAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Additional attachments state
  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);

  // Determine base path based on current route and user role
  const isAccountsPath = location.pathname.startsWith('/accounts');
  const isManagerPath = location.pathname.startsWith('/manager');
  const isInternalAuditPath = location.pathname.startsWith('/internal-audit');
  const isFinanceHeadPath = location.pathname.startsWith('/finance-head');
  
  // Use referrer from location state or determine from user role for admin
  const referrer = location.state?.from;
  
  // Determine the correct back path
  const getBasePath = () => {
    // If we have a referrer in state, use it
    if (referrer) return referrer;
    
    // Otherwise determine based on path
    if (isAccountsPath) return '/accounts/vouchers';
    if (isManagerPath) return '/manager/vouchers';
    if (isInternalAuditPath) return '/internal-audit/vouchers';
    if (isFinanceHeadPath) return '/finance-head/vouchers';
    
    // For admin viewing from /vouchers/:id, check if they came from admin page
    if (user?.role === 'admin') return '/admin/vouchers';
    
    return '/vouchers';
  };
  
  const basePath = getBasePath();
  
  // Check if user is accounts and can approve this voucher
  const isAccountsUser = user?.role === 'accounts';
  const canAccountsApprove = isAccountsUser && voucher?.status === 'INTERNAL_AUDIT_APPROVED';
  const canMarkPaid = isAccountsUser && voucher?.status === 'ACCOUNTS_APPROVED';
  
  // Check if user can edit this voucher (creator or admin for DRAFT vouchers)
  const isCreator = voucher?.createdById === user?.id;
  const isAdmin = user?.role === 'admin';
  const canEdit = voucher?.status === 'DRAFT' && (isCreator || isAdmin);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voucherData, logsData] = await Promise.all([
          vouchersApi.getById(id),
          vouchersApi.getAuditLogs(id),
        ]);
        setVoucher(voucherData);
        setAuditLogs(logsData);
      } catch (error) {
        toast.error('Voucher not found');
        navigate(basePath);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, basePath]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const updated = await vouchersApi.submit(id);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher submitted for approval');
      setShowSubmitDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit voucher');
    } finally {
      setSubmitting(false);
    }
  };

  // Accounts approval handlers
  const handleAccountsApprove = async () => {
    setProcessing(true);
    try {
      const updated = await vouchersApi.accountsApprove(id, actionNote);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher approved by accounts');
      setShowApproveDialog(false);
      setActionNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve voucher');
    } finally {
      setProcessing(false);
    }
  };

  const handleAccountsReject = async () => {
    setProcessing(true);
    try {
      const updated = await vouchersApi.accountsReject(id, actionNote);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher rejected by accounts');
      setShowRejectDialog(false);
      setActionNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject voucher');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!paymentReference.trim()) {
      toast.error('Payment reference is required');
      return;
    }
    setProcessing(true);
    try {
      // Upload attachment if provided
      if (paidAttachment) {
        setUploadingAttachment(true);
        try {
          await vouchersApi.uploadPaymentAttachment(id, paidAttachment);
        } catch (uploadError) {
          console.error('Failed to upload attachment:', uploadError);
          // Continue with marking as paid even if attachment upload fails
        }
        setUploadingAttachment(false);
      }

      const updated = await vouchersApi.markPaid(id, paymentReference, actionNote);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher marked as paid');
      setShowPaidDialog(false);
      setActionNote('');
      setPaymentReference('');
      setPaidAttachment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark voucher as paid');
    } finally {
      setProcessing(false);
      setUploadingAttachment(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Additional attachment handlers
  const handleUploadAdditionalAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !voucher?.id) return;

    // Check file size - 5MB limit
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File "${file.name}" exceeds 5MB limit. Please select a smaller file.`);
      e.target.value = '';
      return;
    }

    setUploadingAdditional(true);
    try {
      const result = await vouchersApi.uploadAdditionalAttachment(voucher.id, file);
      // Refresh voucher data to get updated additional attachments
      const updatedVoucher = await vouchersApi.getById(voucher.id);
      setVoucher(updatedVoucher);
      toast.success('Additional attachment uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploadingAdditional(false);
      e.target.value = '';
    }
  };

  const handleDeleteAdditionalAttachment = async (filename) => {
    try {
      await vouchersApi.deleteAdditionalAttachment(voucher.id, filename);
      // Refresh voucher data
      const updatedVoucher = await vouchersApi.getById(voucher.id);
      setVoucher(updatedVoucher);
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!voucher) return null;

  // Separate regular attachments from payment/accounts attachments
  const allAttachments = voucher.attachments?.map((a) => {
    const [filename, originalName] = a.split('|');
    return { filename, originalName: originalName || filename, isPaymentAttachment: filename.startsWith('payment_') };
  }) || [];
  
  const regularAttachments = allAttachments.filter(a => !a.isPaymentAttachment);
  const accountsAttachments = allAttachments.filter(a => a.isPaymentAttachment);

  return (
    <div className="space-y-6" data-testid="voucher-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(basePath)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-semibold tracking-tight font-mono">
                {voucher.voucherNo || 'Draft Voucher'}
              </h1>
              <StatusBadge status={voucher.status} />
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Created {formatDateTime(voucher.createdAt)} by {voucher.createdByName}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={handlePrint}
            data-testid="print-button"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={() => navigate(`${basePath}/${id}/edit`)}
                data-testid="edit-button"
              >
                <Edit className="w-4 h-4 mr-2" /> Edit
              </Button>
              {isCreator && (
                <Button
                  onClick={() => setShowSubmitDialog(true)}
                  className="bg-[#6a5da4] hover:bg-[#5a4d94]"
                  data-testid="submit-button"
                >
                  <Send className="w-4 h-4 mr-2" /> Submit for Approval
                </Button>
              )}
            </>
          )}
          {/* Accounts Approve/Reject buttons */}
          {canAccountsApprove && (
            <>
              <Button
                onClick={() => setShowRejectDialog(true)}
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="accounts-reject-button"
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                onClick={() => setShowApproveDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="accounts-approve-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
            </>
          )}
          {/* Mark as Paid button */}
          {canMarkPaid && (
            <Button
              onClick={() => setShowPaidDialog(true)}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
              data-testid="mark-paid-button"
            >
              <DollarSign className="w-4 h-4 mr-2" /> Mark as Paid
            </Button>
          )}
        </div>
      </div>

      {/* Manager Rejection Note */}
      {voucher.status === 'DRAFT' && voucher.managerNote && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-red-800">Manager Rejection Note:</p>
            <p className="text-sm text-red-700 mt-1">{voucher.managerNote}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Voucher Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                    <p className="font-medium">{formatDate(voucher.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Branch</p>
                    <p className="font-medium">{voucher.branch}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
                    <p className="font-medium">{voucher.department}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beneficiary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Beneficiary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Name</p>
                    <p className="font-medium">{voucher.beneficiaryName}</p>
                  </div>
                </div>
                {voucher.beneficiaryAddress && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Address</p>
                    <p className="font-medium">{voucher.beneficiaryAddress}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                {/* Row 1 */}
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Type</p>
                  <p className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Advance %</p>
                  <p className="font-medium">{voucher.advancePercent !== null && voucher.advancePercent !== undefined ? `${voucher.advancePercent}%` : '-'}</p>
                </div>
                
                {/* Row 2 */}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Currency</p>
                  <p className="font-medium">{voucher.currency}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">PO Number</p>
                  <p className="font-mono">{voucher.poNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">PO Amount</p>
                  <p className="font-mono">{voucher.poAmount ? formatCurrency(voucher.poAmount, voucher.currency) : '-'}</p>
                </div>
              </div>

              {voucher.paymentMethod === 'TRANSFER' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Bank Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Bank Name</p>
                      <p className="font-medium">{voucher.bankName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">IBAN</p>
                      <p className="font-mono text-sm">{voucher.iban}</p>
                    </div>
                  </div>
                </div>
              )}

              {voucher.paymentRefNumber && (
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment Ref</p>
                    <p className="font-mono">{voucher.paymentRefNumber}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Document No.</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Invoice Amount</TableHead>
                    <TableHead className="text-right">Payable Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="font-mono">{item.invoiceNo || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.note || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount, item.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.payableAmount || 0, item.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3}>Total Payable Amount</TableCell>
                    <TableCell className="text-right font-mono text-lg text-[#6a5da4]">
                      {formatCurrency(voucher.amountTotal, voucher.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {voucher.amountWords && (
                <div className="px-6 py-4 border-t bg-gray-50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Amount in Words</p>
                  <p className="font-medium mt-1">{voucher.amountWords}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Regular Attachments */}
          {regularAttachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {regularAttachments.map((attachment) => (
                    <div
                      key={attachment.filename}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm">{attachment.originalName}</span>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${attachment.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#6a5da4] hover:underline text-sm flex items-center gap-1"
                        data-testid={`download-${attachment.filename}`}
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Accounts/Payment Attachments */}
          {accountsAttachments.length > 0 && (
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-green-700">Accounts Attachment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {accountsAttachments.map((attachment) => (
                    <div
                      key={attachment.filename}
                      className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm">{attachment.originalName}</span>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${attachment.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:underline text-sm flex items-center gap-1"
                        data-testid={`download-payment-${attachment.filename}`}
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Attachments - Can be uploaded anytime by creator */}
          {(voucher.createdById === user?.id || user?.role === 'admin') && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Upload Button */}
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUploadAdditionalAttachment}
                      disabled={uploadingAdditional}
                      data-testid="additional-attachment-input"
                    />
                    <div className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <FileText className="w-5 h-5 text-blue-500" />
                      <div>
                        <span className="text-sm font-medium text-blue-700">
                          {uploadingAdditional ? 'Uploading...' : 'Click to upload additional files'}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Can upload anytime, even after submission (Max 5MB)
                        </p>
                      </div>
                    </div>
                  </label>

                  {/* List of additional attachments */}
                  {voucher.additionalAttachments?.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {voucher.additionalAttachments.map((a) => {
                        const parts = a.split('|');
                        const filename = parts[0];
                        const originalName = parts[1] || filename;
                        const uploadedAt = parts[2];
                        return (
                          <div
                            key={filename}
                            className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <div>
                                <span className="text-sm font-medium">{originalName}</span>
                                {uploadedAt && (
                                  <p className="text-xs text-muted-foreground">
                                    Uploaded: {formatDateTime(uploadedAt)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                                data-testid={`download-additional-${filename}`}
                              >
                                <Download className="w-4 h-4" /> Download
                              </a>
                              <button
                                onClick={() => handleDeleteAdditionalAttachment(filename)}
                                className="text-red-500 hover:text-red-700 p-1"
                                data-testid={`delete-additional-${filename}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Show additional attachments to other users (view only) */}
          {voucher.createdById !== user?.id && user?.role !== 'admin' && voucher.additionalAttachments?.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.additionalAttachments.map((a) => {
                    const parts = a.split('|');
                    const filename = parts[0];
                    const originalName = parts[1] || filename;
                    const uploadedAt = parts[2];
                    return (
                      <div
                        key={filename}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="text-sm font-medium">{originalName}</span>
                            {uploadedAt && (
                              <p className="text-xs text-muted-foreground">
                                Uploaded: {formatDateTime(uploadedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Amount Summary */}
          <Card className="bg-[#6a5da4] text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Total Amount</p>
                  <p className="text-3xl font-mono font-semibold">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                <div>
                  <p className="text-xs opacity-60 uppercase">Items</p>
                  <p className="font-semibold">{voucher.items?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs opacity-60 uppercase">Currency</p>
                  <p className="font-semibold">{voucher.currency}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Status */}
          {voucher.assignedManagerName && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Approval Workflow</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned Manager</p>
                  <p className="font-medium">{voucher.assignedManagerName}</p>
                </div>
                {voucher.submittedAt && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Submitted At</p>
                    <p className="font-medium">{formatDateTime(voucher.submittedAt)}</p>
                  </div>
                )}
                {voucher.managerActionAt && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Manager Action</p>
                    <p className="font-medium">{formatDateTime(voucher.managerActionAt)}</p>
                  </div>
                )}
                {voucher.accountsActionAt && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Accounts Action</p>
                    <p className="font-medium">{formatDateTime(voucher.accountsActionAt)}</p>
                  </div>
                )}
                {voucher.paidAt && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Paid At</p>
                    <p className="font-medium">{formatDateTime(voucher.paidAt)}</p>
                    {voucher.paymentReference && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Ref: {voucher.paymentReference}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={auditLogs} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Voucher for Approval</AlertDialogTitle>
            <AlertDialogDescription>
              This voucher will be sent to your manager for approval. You won't be able to edit it
              after submission. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#6a5da4] hover:bg-[#5a4d94]"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Accounts Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Voucher</DialogTitle>
            <DialogDescription>
              Approve this voucher for payment processing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="approve-note">Note (Optional)</Label>
              <Textarea
                id="approve-note"
                placeholder="Add any notes for approval..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleAccountsApprove} disabled={processing} className="bg-green-600 hover:bg-green-700">
              {processing ? 'Approving...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accounts Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Voucher</DialogTitle>
            <DialogDescription>
              Reject this voucher and return it for correction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-note">Reason for Rejection *</Label>
              <Textarea
                id="reject-note"
                placeholder="Provide reason for rejection..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleAccountsReject} disabled={processing || !actionNote.trim()} variant="destructive">
              {processing ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={showPaidDialog} onOpenChange={setShowPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>
              Record payment details for this voucher.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="payment-ref">Payment Reference *</Label>
              <Input
                id="payment-ref"
                placeholder="Enter payment reference number..."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                required
                data-testid="payment-ref-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paid-attachment">Attachment (Optional)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="paid-attachment"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size > 5 * 1024 * 1024) {
                      toast.error(`File "${file.name}" exceeds 5MB limit. Please select a smaller file.`);
                      e.target.value = '';
                      return;
                    }
                    setPaidAttachment(file || null);
                  }}
                  className="flex-1"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  data-testid="paid-attachment-input"
                />
              </div>
              {paidAttachment && (
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-sm">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{paidAttachment.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setPaidAttachment(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Supported: PDF, Images, Word, Excel (Max 5MB)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paid-note">Note (Optional)</Label>
              <Textarea
                id="paid-note"
                placeholder="Add any payment notes..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPaidDialog(false);
              setPaidAttachment(null);
            }} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid} disabled={processing || !paymentReference.trim()} className="bg-[#6a5da4] hover:bg-[#5a4d94]">
              {processing ? (uploadingAttachment ? 'Uploading...' : 'Processing...') : 'Mark as Paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Template - Hidden on screen, visible on print */}
      <VoucherPrintTemplate voucher={voucher} />
    </div>
  );
};

export default VoucherDetailPage;
